import React, {ReactNode, useContext, useEffect, useMemo} from 'react'
import {Basic} from 'types'
import {InputProvider} from './InputProvider/InputProvider'
import {VentrataData, VentrataDataProvider} from './VentrataDataProvider'

type Props = {
  children: ReactNode
}

type CompletableStep =
  | {name: 'unknown'}
  | {name: 'optionId'; optionId: string}
  | {name: 'availabilityId'; availabilityId: string}
  | {name: 'nothing'}

const canComplete = (isPending: boolean, input: Basic.CartItemInput, ventrataData: VentrataData): CompletableStep => {
  if (isPending) {
    return {name: 'unknown'}
  }

  if (
    ventrataData.product &&
    ventrataData.product.options.length === 1 &&
    input.date &&
    (!input.optionId || input.optionId !== ventrataData.product.options[0].id)
  ) {
    return {name: 'optionId', optionId: ventrataData.product.options[0].id}
  }

  if (input.optionId && ventrataData.availabilities?.[input.optionId]) {
    const availableSlots = ventrataData.availabilities[input.optionId].filter((availability) => availability.available)

    if (availableSlots.length === 1 && (!input.availabilityId || input.availabilityId !== availableSlots[0].id)) {
      return {name: 'availabilityId', availabilityId: availableSlots[0].id}
    }
  }

  return {name: 'nothing'}
}

export const AutocompleterContext = React.createContext(false)

export const Autocompleter = ({children}: Props) => {
  const isPending = VentrataDataProvider.useIsPending()
  const ventrataData = VentrataDataProvider.useData()
  const input = InputProvider.useInput()
  const {setOptionId, setAvailabilityId} = InputProvider.useMutators()
  const completableStep = useMemo(() => canComplete(isPending, input, ventrataData), [isPending, input, ventrataData])

  useEffect(() => {
    switch (completableStep.name) {
      case 'unknown':
      case 'nothing':
        return

      case 'optionId':
        setOptionId(completableStep.optionId)
        return

      case 'availabilityId':
        setAvailabilityId(completableStep.availabilityId)
        return
    }
  }, [completableStep, setOptionId, setAvailabilityId])

  return (
    <AutocompleterContext.Provider value={completableStep.name === 'nothing'}>{children}</AutocompleterContext.Provider>
  )
}

Autocompleter.useIsCompleted = () => {
  return useContext(AutocompleterContext)
}
